import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { BUSINESS } from "@/lib/site";

const QUICK_CHIPS = ["What can you make?", "How do quotes work?", "Do you do team orders?", "How do I send my logo?"];

function getSessionId() {
  let id = localStorage.getItem("stlc_chat_session");
  if (!id) {
    id = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("stlc_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text || draft).trim();
    if (!msg || streaming) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sessionRef.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const d = JSON.parse(line.slice(6));
          if (d.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + d.delta };
              return copy;
            });
          }
          if (d.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text || d.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          text: copy[copy.length - 1].text || "I'm offline right now — please email us and we'll reply fast.",
        };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        data-testid="chat-toggle-button"
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-ember text-ink shadow-[0_14px_38px_-8px_rgba(224,122,95,0.6)] transition-transform duration-200 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" aria-hidden /> : <MessageCircle className="h-6 w-6" aria-hidden />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[88px] right-5 z-40 flex h-[540px] max-h-[calc(100vh-120px)] w-[380px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink/95 shadow-2xl backdrop-blur-xl"
            data-testid="chat-panel"
            role="dialog"
            aria-label="Chat with Skye, our studio assistant"
          >
            <header className="flex items-center gap-3 border-b border-white/10 bg-panel/80 px-4 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ember/15">
                <Sparkles className="h-4.5 w-4.5 text-gold" aria-hidden />
              </span>
              <div className="flex-1">
                <p className="font-display text-sm font-bold text-white">Skye</p>
                <p className="text-[11px] text-mist">Studio assistant · {BUSINESS.name.split(" ").slice(0, 3).join(" ")}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="chat-close-button"
                className="rounded-full p-1.5 text-mist transition-colors duration-200 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4.5 w-4.5" aria-hidden />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <p className="rounded-2xl rounded-tl-sm bg-panel2 px-4 py-3 text-sm leading-relaxed text-white/90">
                    Hey! I'm Skye — ask me anything about what we make, and I'll point you to the fastest way to get
                    it quoted.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_CHIPS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => send(c)}
                        data-testid={`chat-quick-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-mist transition-colors duration-200 hover:border-gold/50 hover:text-white"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-br-sm bg-ember text-ink"
                      : "rounded-tl-sm bg-panel2 text-white/90"
                  }`}
                >
                  {m.text || (streaming && i === messages.length - 1 ? <TypingDots /> : "")}
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-panel/80 p-3"
              data-testid="chat-input-form"
            >
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask about a project…"
                aria-label="Message"
                data-testid="chat-input"
                className="field !py-2.5"
              />
              <button
                type="submit"
                disabled={streaming || !draft.trim()}
                aria-label="Send message"
                data-testid="chat-send-button"
                className="btn-primary !rounded-full !p-3"
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            </form>
            <p className="bg-panel/80 px-4 pb-3 text-center text-[10px] leading-relaxed text-mist/70">
              AI assistant — answers are general guidance. For exact pricing, request a quote; a real person always
              replies.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function TypingDots() {
  return (
    <span className="flex gap-1" aria-label="Assistant is typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-mist" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}
